/**
 * Route guard for protected routes.
 *
 * Redirects unauthenticated users to sign-in page.
 * Shows loading state while auth is being checked.
 */

import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Skeleton } from "@cloch-ai/ui";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isClerkConfigured } from "../providers/ClerkProvider";

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Loading skeleton for auth check.
 */
function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="space-y-4 w-64">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

/**
 * Inner auth guard that uses Clerk hooks.
 * Only rendered when Clerk is configured.
 */
function AuthGuardInner({ children }: AuthGuardProps) {
  const { isLoaded } = useAuth();
  const location = useLocation();

  // Wait for auth to load
  if (!isLoaded) {
    return <AuthLoading />;
  }

  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" state={{ from: location }} replace />
      </SignedOut>
    </>
  );
}

/**
 * Auth guard component.
 *
 * Usage:
 * ```tsx
 * <AuthGuard>
 *   <ProtectedPage />
 * </AuthGuard>
 * ```
 */
export function AuthGuard({ children }: AuthGuardProps) {
  // If Clerk is not configured, allow access (development mode)
  if (!isClerkConfigured()) {
    return <>{children}</>;
  }

  return <AuthGuardInner>{children}</AuthGuardInner>;
}

/**
 * Higher-order component for protected pages.
 */
export function withAuth<P extends object>(Component: React.ComponentType<P>) {
  return function WrappedComponent(props: P) {
    return (
      <AuthGuard>
        <Component {...props} />
      </AuthGuard>
    );
  };
}
